import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, publicAssetUrl, errorMessage } from "@/lib/api";
import { MerchantLayout } from "@/pages/merchant/MerchantLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TicketCheck, TicketPercent, CheckCircle2, XCircle, Clock, ScanLine } from "lucide-react";

const STATUS_TONE = {
    CLAIMED: "bg-orange-50 text-orange-700 border-orange-200",
    REDEEMED: "bg-emerald-50 text-emerald-700 border-emerald-200",
    EXPIRED: "bg-neutral-100 text-neutral-500 border-neutral-200",
};

export default function MerchantCoupons() {
    const [tab, setTab] = useState("ALL");
    const [claims, setClaims] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [code, setCode] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const load = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams({ limit: "100" });
            if (tab !== "ALL") params.set("status", tab);
            const res = await api.get(`/me/coupon-claims?${params.toString()}`);
            setClaims(res.data.claims || []);
            setTotal(res.data.total || 0);
        } catch (e) {
            toast.error("Could not load coupons");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load(); // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    const redeem = async () => {
        const c = (code || "").trim().toUpperCase();
        if (!c) return;
        try {
            setBusy(true);
            const r = await api.post(`/merchant/coupons/redeem`, { code: c });
            setResult({ ok: true, ...r.data });
            toast.success(r.data.status === "REDEEMED" ? "Redeemed ✔" : r.data.status);
            setCode("");
            await load();
        } catch (e) {
            const msg = errorMessage(e);
            setResult({ ok: false, message: msg });
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    };

    const claimedCount = useMemo(
        () => claims.filter((c) => c.status === "CLAIMED").length,
        [claims],
    );

    return (
        <MerchantLayout>
            <div className="px-4 pb-6 pt-4" data-testid="merchant-coupons-page">
                <div className="mb-3">
                    <div className="text-xs font-semibold uppercase tracking-wide text-orange-600">
                        Coupons
                    </div>
                    <h1 className="text-2xl font-extrabold text-neutral-900">Redeem coupons</h1>
                    <p className="mt-1 text-xs text-neutral-500">
                        Enter the code shared by the customer and validate at the counter.
                    </p>
                </div>

                <Card className="rounded-2xl border border-orange-100 bg-orange-50 p-4" data-testid="coupons-redeem-card">
                    <div className="mb-2 flex items-center justify-between gap-2 text-sm font-bold">
                        <div className="flex items-center gap-2">
                            <ScanLine className="text-orange-600" size={16} />
                            Redeem a code
                        </div>
                        <Link to="/merchant/coupons/scan" className="text-xs font-semibold text-orange-700 hover:underline" data-testid="coupons-scan-link">
                            Open QR scanner →
                        </Link>
                    </div>
                    <div className="flex items-center gap-2">
                        <Input
                            className="h-12 bg-white text-base uppercase tracking-wider"
                            placeholder="ABCD-EFGH-JKMN"
                            value={code}
                            onChange={(e) => setCode(e.target.value.toUpperCase())}
                            data-testid="coupons-redeem-input"
                        />
                        <Button
                            disabled={!code || busy}
                            onClick={redeem}
                            className="h-12 nest-gradient font-semibold text-white"
                            data-testid="coupons-redeem-btn"
                        >
                            Redeem
                        </Button>
                    </div>
                    {result && (
                        <div
                            className={`mt-3 flex items-start gap-2 rounded-xl p-3 text-xs ${result.ok ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-800"}`}
                            data-testid="coupons-redeem-result"
                        >
                            {result.ok ? (
                                <CheckCircle2 className="mt-0.5" size={14} />
                            ) : (
                                <XCircle className="mt-0.5" size={14} />
                            )}
                            <div>
                                {result.ok
                                    ? result.status === "REDEEMED"
                                        ? `Code ${result.code} redeemed successfully.`
                                        : `This code was already redeemed.`
                                    : result.message}
                            </div>
                        </div>
                    )}
                </Card>

                <div className="mt-6 flex items-end justify-between">
                    <div>
                        <div className="text-sm font-bold text-neutral-800">Claims inbox</div>
                        <div className="text-[11px] text-neutral-500">
                            {claimedCount} pending redemption · {total} total
                        </div>
                    </div>
                </div>

                <Tabs value={tab} onValueChange={setTab} className="mt-2">
                    <TabsList className="grid w-full grid-cols-3" data-testid="coupons-filter-tabs">
                        <TabsTrigger value="ALL" className="text-xs">All</TabsTrigger>
                        <TabsTrigger value="CLAIMED" className="text-xs">Pending</TabsTrigger>
                        <TabsTrigger value="REDEEMED" className="text-xs">Redeemed</TabsTrigger>
                    </TabsList>
                </Tabs>

                <div className="mt-3 space-y-2">
                    {loading ? (
                        Array.from({ length: 4 }).map((_, i) => (
                            <Skeleton key={i} className="h-20 rounded-2xl" />
                        ))
                    ) : claims.length ? (
                        claims.map((c) => <ClaimRow key={c.code} c={c} />)
                    ) : (
                        <div className="rounded-2xl border border-dashed border-neutral-300 bg-neutral-50 p-8 text-center">
                            <TicketPercent className="mx-auto mb-2 text-neutral-400" size={22} />
                            <div className="text-sm font-semibold text-neutral-700">No coupons yet</div>
                            <div className="mt-1 text-xs text-neutral-500">
                                Enable coupons on your offers to start collecting claims.
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </MerchantLayout>
    );
}

function ClaimRow({ c }) {
    const disc = c.discount_snapshot;
    const discountText = disc
        ? disc.type === "PERCENT"
            ? `${disc.value}% off`
            : `₹${disc.value} off`
        : null;
    return (
        <Link
            to={`/merchant/offers/${c.post_id}`}
            className="flex items-center gap-3 rounded-2xl border border-neutral-200 bg-white p-3 hover:bg-neutral-50"
            data-testid={`coupon-row-${c.code}`}
        >
            <div className="h-14 w-14 shrink-0 overflow-hidden rounded-xl bg-neutral-100">
                {c.post_poster_image_url ? (
                    <img
                        src={publicAssetUrl(c.post_poster_image_url)}
                        alt=""
                        className="h-full w-full object-cover"
                    />
                ) : (
                    <div className="flex h-full w-full items-center justify-center text-neutral-400">
                        <TicketCheck size={18} />
                    </div>
                )}
            </div>
            <div className="min-w-0 flex-1">
                <div className="font-mono text-sm font-bold tracking-wide text-neutral-800">
                    {c.code}
                </div>
                <div className="mt-0.5 truncate text-[11px] text-neutral-500">
                    {c.post_title}
                    {discountText && <> · {discountText}</>}
                </div>
                <div className="mt-1 flex items-center gap-2 text-[11px] text-neutral-500">
                    <Clock size={11} />
                    {new Date(c.claimed_at).toLocaleString()}
                    {c.mobile_masked && <span>· {c.mobile_masked}</span>}
                </div>
            </div>
            <Badge variant="outline" className={`shrink-0 text-[10px] ${STATUS_TONE[c.status]}`}>
                {c.status}
            </Badge>
        </Link>
    );
}
